declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

// Message types for React Native communication
type RequestMessage = 
  | { type: 'AUTH_REFRESH_TOKENS'; requestId: string }
  | { type: 'AUTH_SIGN_OUT'; requestId: string };

type ResponseMessage = 
  | { type: 'AUTH_RESPONSE'; requestId: string; data: AuthTokens }
  | { type: 'AUTH_ERROR'; requestId: string; data: { error: string } };

interface PendingRequest {
  resolve: (value: AuthTokens) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
}

/**
 * Native Authentication Bridge
 * 
 * Handles communication between the web app and React Native for authentication.
 * React Native handles sign-in natively by closing the WebView when tokens are expired.
 * This bridge only handles token refresh and sign-out operations.
 */
class NativeAuthBridge {
  private pendingRequests = new Map<string, PendingRequest>();

  private messageHandler: ((event: MessageEvent) => void) | null = null;

  constructor() {
    this.setupMessageListener();
  }

  private setupMessageListener(): void {
    if (this.messageHandler) {
      return; // Already set up
    }

    this.messageHandler = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as ResponseMessage;
        this.handleMessage(message);
      } catch (error) {
        console.error('[NativeAuthBridge] Failed to parse message:', error);
      }
    };

    window.addEventListener('message', this.messageHandler);
  }

  private handleMessage(message: ResponseMessage): void {
    const { requestId } = message;
    const pendingRequest = this.pendingRequests.get(requestId);

    if (!pendingRequest) {
      console.warn('[NativeAuthBridge] Received response for unknown request:', requestId);
      return;
    }

    // Clean up
    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(requestId);

    if (message.type === 'AUTH_RESPONSE') {
      console.log('[NativeAuthBridge] Received successful response for request:', requestId);
      pendingRequest.resolve(message.data);
    } else if (message.type === 'AUTH_ERROR') {
      console.error('[NativeAuthBridge] Received error response for request:', requestId, message.data.error);
      pendingRequest.reject(new Error(message.data.error));
    }
  }

  private sendMessage(message: RequestMessage): Promise<AuthTokens> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(message.requestId);
        reject(new Error('Request timeout'));
      }, 30000); // 30 second timeout

      this.pendingRequests.set(message.requestId, {
        resolve,
        reject,
        timeout,
      });

      // Send message to React Native
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      } else {
        // Fallback for testing or non-React Native environments
        window.postMessage(JSON.stringify(message), '*');
      }
    });
  }

  /**
   * Refresh authentication tokens
   * 
   * Requests fresh tokens from React Native. If tokens are expired,
   * React Native will close the WebView and show native login screen.
   */
  async refreshTokens(): Promise<AuthTokens> {
    const requestId = `refresh_${Date.now()}_${Math.random()}`;
    console.log('[NativeAuthBridge] Requesting token refresh:', requestId);

    const message: RequestMessage = {
      type: 'AUTH_REFRESH_TOKENS',
      requestId,
    };

    return this.sendMessage(message);
  }

  /**
   * Sign out the current user
   * 
   * Requests React Native to sign out the user and clear all tokens.
   */
  async signOut(): Promise<void> {
    const requestId = `signout_${Date.now()}_${Math.random()}`;
    console.log('[NativeAuthBridge] Requesting sign out:', requestId);

    const message: RequestMessage = {
      type: 'AUTH_SIGN_OUT',
      requestId,
    };

    await this.sendMessage(message);
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    // Clear any pending requests
    this.pendingRequests.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingRequests.clear();
  }
}

// Global instance
let nativeAuthBridge: NativeAuthBridge | null = null;

export const getNativeAuthBridge = (): NativeAuthBridge => {
  if (!nativeAuthBridge) {
    nativeAuthBridge = new NativeAuthBridge();
  }
  return nativeAuthBridge;
};

export { NativeAuthBridge };
